import { createContext, PropsWithChildren, useCallback, useMemo, useRef, useState, useEffect } from "react";
import { StyleSheet, View, ViewStyle } from "react-native";
import { Toast, ToastProps } from "@/components/Toast";
import AsyncStorage from "@react-native-async-storage/async-storage";
import type { ToastLocation, ToastProviderProps } from "@/types/providers/toast";

export const ToastContext = createContext<ToastProviderProps | undefined>(undefined);

type Item = ToastProps & { id: string };

const getPositionStyle = (location: ToastLocation): ViewStyle => {
  const style: ViewStyle = {};
  if (location.startsWith("top")) style.top = 16;
  else style.bottom = 16;
  if (location.endsWith("left")) style.left = 16;
  else if (location.endsWith("right")) style.right = 16;
  else {
    style.left = "50%";
    style.transform = [{ translateX: "-50%" }];
  }
  return style;
};

export function ToastProvider({ children }: PropsWithChildren) {
  const [items, setItems] = useState<Item[]>([]);
  const [toastLocation, setToastLocation] = useState<ToastLocation>("bottom-right");
  const counterRef = useRef(0);

  useEffect(() => {
    AsyncStorage.getItem("toast_location").then((value) => {
      if (value) setToastLocation(value as ToastLocation);
    });
  }, []);

  const removeById = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback<ToastProviderProps["showToast"]>((t) => {
    const id = `t_${Date.now()}_${counterRef.current++}`;
    setItems((prev) => {
      // light dedupe: same title+message within last 5 toasts
      const last = prev.slice(-5);
      if (last.some((x) => x.title === t.title && x.message === t.message && x.toastType === t.toastType)) {
        return prev;
      }
      const next: Item = { id, ...t };
      const capped = [...prev, next];
      // cap stack to 6 (drop oldest)
      if (capped.length > 6) capped.shift();
      return capped;
    });
    return id;
  }, []);

  const clearAll = useCallback(() => setItems([]), []);

  // Global RN error trap
  useEffect(() => {
    // @ts-ignore
    const EU = global?.ErrorUtils;
    if (!EU || !EU.setGlobalHandler) return;
    const prev = EU._globalHandler;
    EU.setGlobalHandler((error: Error, isFatal?: boolean) => {
      showToast({
        title: isFatal ? "Fatal error" : "Error",
        message: error?.message,
        toastType: isFatal ? "fatal" : "error",
        duration: 10,
      });
      if (typeof prev === "function") prev(error, isFatal);
    });
  }, [showToast]);

  const ctx = useMemo(() => ({ showToast, clearAll, setToastLocation }), [showToast, clearAll]);

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <View style={[styles.container, getPositionStyle(toastLocation)]} pointerEvents="box-none">
        {items.map((t) => (
          <Toast key={t.id} {...t} onRequestClose={() => removeById(t.id)} />
        ))}
      </View>
    </ToastContext.Provider>
  );
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    width: 420,
    maxWidth: "92%",
    gap: 12,
    pointerEvents: "box-none",
  },
});
